import React, { useState } from 'react'
import Close from '@Images/close_tiny.svg'
import classnames from 'classnames'

import {
  IExternalToken,
} from '@SiteModules/ExternalToken/types'

import './ExternalTokenForm.scss'

const DEFAULT_VALIDITY_TIME_IN_DAYS = 30
const MINIMUM_VALIDITY_TIME_IN_DAYS = 1
const TEN_YEARS_IN_DAYS = 3650
const EXPIRATION_DATE_FORMATTER = new Intl.DateTimeFormat('en', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

interface ExternalTokenFormProps {
  readonly loading: boolean
  readonly externalTokens: IExternalToken[]
  readonly renderedFromDashboard: boolean
  readonly title?: string
  readonly description?: string
  readonly createButtonTestId?: string
  readonly copyButtonTestId?: string
  readonly expiryInputTestId?: string
  readonly addExpiryDateTestId?: string
  readonly removeExpiryDateTestId?: string
  readonly deleteButtonTestId?: string
  copyToClipboard(externalTokenUrl: string): void
  deleteExternalToken(externalTokenId: string): void
  setValidityOfExternalToken(validityInDays: number): void
}

const formatExpirationDate = (expiresAt?: string | null) => {
  if (!expiresAt) { return undefined }

  return EXPIRATION_DATE_FORMATTER.format(new Date(expiresAt))
}

const ExternalTokenForm: React.FC<ExternalTokenFormProps> = props => {
  const {
    loading,
    externalTokens,
    renderedFromDashboard,
    title,
    description,
  } = props

  const [hasDefinedValidity, setHasDefinedValidity] = useState(false)
  const [validity, setValidity] = useState(DEFAULT_VALIDITY_TIME_IN_DAYS.toString())
  const hasExternalTokens = externalTokens.length > 0

  const setExpiryDate = () => {
    setValidity(DEFAULT_VALIDITY_TIME_IN_DAYS.toString())
    setHasDefinedValidity(true)
  }

  const setExpiryDateFarInTheFuture = () => {
    setValidity(TEN_YEARS_IN_DAYS.toString())
    setHasDefinedValidity(false)
  }

  const createExternalToken = () => {
    if (hasDefinedValidity) {
      props.setValidityOfExternalToken(Math.max(Math.min(parseInt(validity, 10) || DEFAULT_VALIDITY_TIME_IN_DAYS, TEN_YEARS_IN_DAYS), MINIMUM_VALIDITY_TIME_IN_DAYS))
    } else {
      props.setValidityOfExternalToken(TEN_YEARS_IN_DAYS)
    }
  }

  const onChangeValidity = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement
    if (target.value) {
      setValidity(target.value)
    } else {
      setValidity('')
    }
  }

  const copyExternalToken = (e: React.MouseEvent<HTMLButtonElement>) => {
    const externalTokenUrl = e.currentTarget.dataset.externalTokenUrl
    if (!externalTokenUrl) { return }

    props.copyToClipboard(externalTokenUrl)
  }

  const deleteExternalToken = (e: React.MouseEvent<HTMLButtonElement>) => {
    const externalTokenId = e.currentTarget.dataset.externalTokenId
    if (!externalTokenId) { return }

    props.deleteExternalToken(externalTokenId)
  }

  return (
    <div className="ExternalTokenForm">
      <div className="ExternalTokenForm__title">{title || 'Share public link'}</div>
      {description && (
        <div className="ExternalTokenForm__description">
          {description}
        </div>
      )}
      {hasExternalTokens ? (
        <React.Fragment>
          <div className="ExternalTokenForm__expiry_date_wrapper">
            {hasDefinedValidity ? (
              <div
                className={classnames(
                  'ExternalTokenForm__expiry_date',
                  {'ExternalTokenForm__expiry_date-dashboard': renderedFromDashboard},
                )}
              >
                Access expires in
                <span className="ExternalTokenForm__input-wrapper">
                  <input
                    type="number"
                    name="validity"
                    min="1"
                    max="3650"
                    value={validity}
                    onChange={onChangeValidity}
                    className="ExternalTokenForm__input"
                    data-testid={props.expiryInputTestId}
                  />
                </span>
                days
                <button
                  type="button"
                  className="ExternalTokenForm__remove_expiry_date"
                  onClick={setExpiryDateFarInTheFuture}
                  aria-label="Remove expiry date"
                  data-testid={props.removeExpiryDateTestId}
                >
                  <Close />
                </button>
              </div>
            ) : (
              <button
                type="button"
                className={classnames(
                  'ExternalTokenForm__add_expiry_date',
                  {'ExternalTokenForm__add_expiry_date-dashboard': renderedFromDashboard},
                )}
                onClick={setExpiryDate}
                data-testid={props.addExpiryDateTestId}
              >
                Add expiry date
              </button>
            )}
            <button
              type="button"
              className="ExternalTokenForm__create-link"
              onClick={createExternalToken}
              disabled={loading}
              data-testid={props.createButtonTestId || 'external-token-create-button'}
            >
              Create public link
            </button>
          </div>

          <div className="ExternalTokenForm__links">
            <div className="ExternalTokenForm__links-title">Active public links</div>
            {externalTokens.map((externalToken, index) => (
              <div className="ExternalTokenForm__link" key={externalToken.id}>
                <div className="ExternalTokenForm__link-copy">
                  <div className="ExternalTokenForm__link-header">
                    <span>{`Public access link ${index + 1}`}</span>
                    {externalToken.attributes.expiresAt && (
                      <span className="ExternalTokenForm__expiry-badge">
                        {`Expires ${formatExpirationDate(externalToken.attributes.expiresAt)}`}
                      </span>
                    )}
                  </div>
                  <span>{externalToken.attributes.externalTokenUrl}</span>
                </div>
                <div className="ExternalTokenForm__link-actions">
                  <button
                    type="button"
                    className="ExternalTokenForm__copy-link"
                    onClick={copyExternalToken}
                    data-external-token-url={externalToken.attributes.externalTokenUrl}
                    data-testid={props.copyButtonTestId ? `${props.copyButtonTestId}-${externalToken.id}` : undefined}
                  >
                    Copy link
                  </button>
                  <button
                    type="button"
                    className="ExternalTokenForm__delete-link"
                    onClick={deleteExternalToken}
                    disabled={loading}
                    data-external-token-id={externalToken.id}
                    data-testid={props.deleteButtonTestId ? `${props.deleteButtonTestId}-${externalToken.id}` : undefined}
                  >
                    Remove link
                  </button>
                </div>
              </div>
            ))}
          </div>
        </React.Fragment>
      ) : (
        <button
          type="button"
          className="ExternalTokenForm__create-link"
          onClick={createExternalToken}
          disabled={loading}
          data-testid={props.createButtonTestId || 'external-token-create-button'}
        >
          Create public link
        </button>
      )}
    </div>
  )
}

export default ExternalTokenForm
